import { SkipLimitPageSpec } from '@warebee/shared/athena-db-model';
import { StringSearchFilterType } from '@warebee/shared/data-access-api-dto';
import { SortDirection } from '@warebee/shared/engine-model';
import { sql } from 'kysely';
import _ from 'lodash';
import {
  ActualityHqDataRow,
  getActualityHqQueryBuilder,
} from '../actuality/cteQueryBuilders/actualityHqQueryBuilder';
import { ActualityCongestionsParams } from './actualityCongestion';

// export type ActualityHqWithCongestionsRows = InferResult<
//   ReturnType<typeof getActualityHqWithCongestions>
// >;

// export type ActualityHqWithCongestionsRow =
//   ActualityHqWithCongestionsRows[number];
export type ActualityHqWithCongestionsRow = ActualityHqDataRow & {
  congestedEvents: string[];
  congestedLocations: string[];
  congestedJobs: string[];
  congestedAgentIds: string[];
  congestedEventTime: string[];
};

export type ActualityHqWithCongestionsRows = ActualityHqWithCongestionsRow[];
export type ActualityHqWithCongestionsField =
  keyof ActualityHqWithCongestionsRow;
export type ActualityHqWithCongestionsSortBy = {
  direction?: SortDirection;
  field: ActualityHqWithCongestionsField;
};
export type ActualityHqWithCongestionsPagedClientParams = {
  congestedOnly?: boolean;
  sortBy?: ActualityHqWithCongestionsSortBy[];
  page?: SkipLimitPageSpec;
};

export type ActualityHqWithCongestionsParams = ActualityCongestionsParams &
  ActualityHqWithCongestionsPagedClientParams;

export function getActualityHqWithCongestions(
  params: ActualityHqWithCongestionsParams,
) {
  let query = getActualityHqQueryBuilder(params)
    .with(
      cte => cte('hqwc'),
      db =>
        db
          .selectFrom('hq')
          .leftJoinLateral(
            eb =>
              eb
                .selectFrom('hq as hqc')
                .select(({ fn, cast }) => [
                  fn
                    .agg<
                      string[]
                    >('array_agg', [cast(sql.ref('hqc.eventId'), 'json')])
                    .as('congestedEvents'),
                  fn
                    .agg<string[]>('array_agg', [
                      cast(sql.ref('hqc.locationId'), 'json'),
                    ])
                    .distinct()
                    .as('congestedLocations'),
                  fn
                    .agg<string[]>('array_agg', [
                      cast(sql.ref('hqc.jobId'), 'json'),
                    ])
                    .distinct()
                    .as('congestedJobs'),
                  fn
                    .agg<string[]>('array_agg', [
                      cast(sql.ref('hqc.agentId'), 'json'),
                    ])
                    .distinct()
                    .as('congestedAgentIds'),
                  fn
                    .agg<string[]>('array_agg', [
                      cast(sql.ref('hqc.eventEndTime'), 'json'),
                    ])
                    .distinct()
                    .as('congestedEventTime'),
                ])
                .whereRef('hq.agentId', '<>', 'hqc.agentId')
                .where(({ between, fn }) =>
                  between(
                    fn('date_diff', [
                      sql.raw("'second'"),
                      sql.ref('hqc.eventEndTime'),
                      sql.ref('hq.eventEndTime'),
                    ]),
                    0,
                    params.congestionThresholdSeconds,
                  ),
                )
                .where(
                  sql<number>`ST_DISTANCE(
                      ST_Point(${sql.ref('hq.x')},${sql.ref('hq.y')}), 
                      ST_Point(${sql.ref('hqc.x')},${sql.ref('hqc.y')})) `,
                  '<=',
                  params.congestionDistance,
                )
                .as('hqc'),
            join => join.onTrue(),
          )
          .selectAll(),
    )
    .selectFrom('hqwc')
    .selectAll()
    .$if(params.congestedOnly, qb =>
      qb.where(
        ({ fn }) => fn<number>('cardinality', [sql.ref('congestedEvents')]),
        '>',
        0,
      ),
    );

  if (params.filterBy) {
    query = _.reduce(
      params.filterBy,
      (q, value, key) => {
        if (
          value?.type === StringSearchFilterType.CONTAINS &&
          value?.value === 'undefined'
        ) {
          return q.where(key as any, 'is', null);
        }

        if (
          value?.type === StringSearchFilterType.CONTAINS &&
          !_.isEmpty(value?.value)
        ) {
          return q.where(key as any, 'like', `%${value.value}%`);
        }
        return q;
      },
      query,
    );
  }

  return query;
}

export function getActualityHqWithCongestionsDataRowsQuery(
  params: ActualityHqWithCongestionsParams,
) {
  let query = getActualityHqWithCongestions(params).$if(
    params.page !== null,
    qb => qb.offset(params.page?.skip ?? 0).limit(params.page?.limit ?? 100),
  );

  _.forEach(params.sortBy, sb => {
    query = query.orderBy(
      sb.field,
      sb.direction === SortDirection.ASC ? 'asc' : 'desc',
    );
  });
  return query;
}

export function getActualityHqWithCongestionsTotalQuery(
  params: ActualityHqWithCongestionsParams,
) {
  let query = getActualityHqWithCongestions(params)
    .clearSelect()
    .select(({ fn }) => [fn.countAll<number>().as('totalCount')]);
  return query;
}
